import { brandFromPathname, DEFAULT_REFERRAL_BRAND, ReferralBrand } from './brand';
import { loadReferralBrand }                                        from './referralBrandStorage';

/**
 * Route first, then the persisted brand, then energy. The route wins over storage because a
 * supplier link is the authoritative statement of which brand the customer belongs to, and a
 * brand left over from earlier browsing in the same tab must not override it.
 */
export const resolveReferralBrand = (pathname: string): ReferralBrand =>
  brandFromPathname(pathname) ?? loadReferralBrand() ?? DEFAULT_REFERRAL_BRAND;

/**
 * Marks an arrival that reached a branded landing page through the legacy /referral URL.
 *
 * AC 14 requires those arrivals to be measurable, and both redirects are otherwise invisible to
 * analytics: a redirect is transparent to the browser, so the customer lands on /energy/referral
 * with nothing to distinguish them from someone who opened that URL directly.
 *
 * The same marker is appended by the nginx 301 in .config/nginx-default.conf. Keep the two in
 * step, and note the SPA is the only side that can be unit-tested.
 */
const REFERRAL_SOURCE_PARAM = 'referralSource';
const LEGACY_REFERRAL_SOURCE = 'legacy';

export const LEGACY_REFERRAL_MARKER = `${REFERRAL_SOURCE_PARAM}=${LEGACY_REFERRAL_SOURCE}`;

/** Whether this landing was reached via the legacy /referral URL, for the analytics dimension. */
export const referralSourceFrom = (search: string): 'legacy' | 'direct' =>
  new URLSearchParams(search).get(REFERRAL_SOURCE_PARAM) === LEGACY_REFERRAL_SOURCE ? 'legacy' : 'direct';

/**
 * Where the legacy /referral URL sends the customer. nginx already 301s cold arrivals to the
 * energy variant; this covers in-app bounces, where the brand is resolved the usual way:
 * route, then persisted, then energy.
 *
 * Note what that fallback means, because this path is reached by an unauthenticated deep link:
 * PrivateRoute bounces /referral/:complaintId/... to /referral, and if sessionStorage is empty
 * (a new tab, so nothing persisted) a communications customer lands on the ENERGY variant and is
 * shown the energy privacy policy. Storage only carries the brand within a tab, and the journey
 * paths have no brand segment to recover it from. Branding those paths is the follow-on story;
 * until then this is the known hole left by dropping the sector-from-lookup backstop.
 *
 * The query string carries the case-reference pre-fill and the return path, so the existing
 * params are concatenated through byte for byte rather than being round-tripped by
 * URLSearchParams, which would re-encode them.
 */
export const legacyReferralRedirectTo = (search: string) => ({
  pathname: `/${resolveReferralBrand('/referral')}/referral`,
  search:   search ? `${search}&${LEGACY_REFERRAL_MARKER}` : `?${LEGACY_REFERRAL_MARKER}`,
});
